// ── V132: 발주처 고르기 ─────────────────────────────────────────────────────
// 단가표에서 발주처 칸을 누르면 지금까지 입력한 발주처 목록이 뜬다 (많이 쓴 순).
// 고르면 발주처와 그 발주처의 중국어 이름이 같이 들어간다. 목록에 없으면 그냥 새로 입력하면 된다.
// 글자를 치면 목록이 좁혀진다 (한/영 안 바꾸고 친 것·중국어 이름으로도).
// 표가 가로 스크롤 상자 안에 있어서 목록은 화면 기준(position: fixed)으로 띄운다 — 상자에 잘리지 않게.
import React, { useEffect, useRef, useState } from 'react';
import { searchVariants } from '../utils/hangulKeys';

export default function VendorPicker({
  value, options = [], onChange, onPick, onKeyDown, onBlur,
  placeholder, style, autoFocus, editRow, countLabel = (n) => `${n}`,
}) {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(true);   // 막 열었을 땐 전체 목록, 글자를 치면 좁힌다
  const [hi, setHi] = useState(-1);
  const [box, setBox] = useState(null);

  const q = String(value || '').trim();
  const list = (() => {
    if (!options.length) return [];
    if (showAll || !q) return options.slice(0, 50);
    const qs = [...new Set(searchVariants(q).map(v => v.toLowerCase()).filter(Boolean))];
    return options.filter(o => qs.some(v => o.name.toLowerCase().includes(v) || (o.zh && o.zh.includes(v)))).slice(0, 50);
  })();

  // 칸 위치에 맞춰 띄운다. (autoFocus로 열릴 땐 ref가 아직 없을 수 있어 이벤트의 칸을 받는다)
  const place = (target) => {
    const el = target || inputRef.current;
    if (!el || !el.getBoundingClientRect) return;
    const r = el.getBoundingClientRect();
    setBox({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 200) });
  };
  const openList = (target) => { place(target); setShowAll(true); setHi(-1); setOpen(true); };

  // 화면이 스크롤되면 위치가 어긋나므로 닫는다
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => { window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close); };
  }, [open]);

  const pick = (o) => {
    if (onPick) onPick(o); else if (onChange) onChange(o.name);
    setOpen(false);
  };

  const rowAttr = editRow ? { 'data-edit-row': true } : {};
  return (
    <>
      <input
        {...rowAttr}
        ref={inputRef}
        value={value || ''}
        autoFocus={autoFocus}
        placeholder={placeholder}
        style={style}
        autoComplete="off"
        onFocus={e => openList(e.currentTarget)}
        onClick={e => { if (!open || !box) openList(e.currentTarget); }}
        onChange={e => { onChange && onChange(e.target.value); setShowAll(false); setHi(-1); if (!open || !box) { place(e.currentTarget); setOpen(true); } }}
        onBlur={e => { setTimeout(() => setOpen(false), 150); onBlur && onBlur(e); }}
        onKeyDown={e => {
          if (open && list.length) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHi(h => Math.min(list.length - 1, h + 1)); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setHi(h => Math.max(0, h - 1)); return; }
            if (e.key === 'Enter' && hi >= 0) { e.preventDefault(); pick(list[hi]); return; }
          }
          if (e.key === 'Escape' && open) { e.preventDefault(); setOpen(false); return; }
          onKeyDown && onKeyDown(e);
        }}
      />
      {open && list.length > 0 && box && (
        <div {...rowAttr} role="listbox"
          style={{
            position: 'fixed', top: box.top, left: box.left, width: box.width, zIndex: 3000,
            background: '#fff', border: '1.5px solid #EA580C', borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.14)', maxHeight: 240, overflowY: 'auto',
          }}>
          {list.map((o, i) => (
            <div key={o.name} role="option" aria-selected={i === hi}
              onMouseDown={e => { e.preventDefault(); pick(o); }}
              onMouseEnter={() => setHi(i)}
              style={{
                padding: '7px 10px', cursor: 'pointer', fontSize: 13,
                background: i === hi ? '#FFF7ED' : (o.name === q ? '#FAFAFA' : '#fff'),
                borderBottom: '1px solid #F4F4F5', display: 'flex', alignItems: 'center', gap: 6,
              }}>
              <span style={{ fontWeight: 700, color: '#18181B' }}>{o.name}</span>
              {o.zh && <span style={{ color: '#1A56A0', fontSize: 12 }}>{o.zh}</span>}
              <span style={{ marginLeft: 'auto', color: '#A1A1AA', fontSize: 11, whiteSpace: 'nowrap' }}>{countLabel(o.count)}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
